import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { Messages } from '../../../codegen/Localize';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { Set } from '../../../ts/models/Set';
import { ActiveDirectoryGroupAction } from '../../../userManagement/groups/services/actions/ActiveDirectoryGroupAction';
import { RemoveUsersFromGroupAction } from '../../../userManagement/groups/services/actions/RemoveUsersFromGroupAction';
import { DeleteGroupAction } from '../../../userManagement/groups/services/actions/DeleteGroupAction';
import { UpdateGroupsMinimumSiteRoleAction } from '../../../userManagement/groups/services/actions/UpdateGroupsMinimumSiteRoleAction';
import { UpdateLocalGroupGrantLicenseModeAction } from '../../../ts/services/UpdateLocalGroupGrantLicenseModeAction';
import { DeleteGroups, GroupAddUsers, GroupRemoveUsers, SyncAdGroup, SyncAdGroups, UpdateGroupsMinimumSiteRole, UpdateLocalGroupsLicenseUponLogin } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { GroupMenuAction, GroupMinimumSiteRoleUpdateAction, GroupsDeleteAction, GroupSyncAction, GroupUserAddAction, LocalGroupLicenseUponLoginUpdateAction } from '../../actions/spaces/GroupMenuActions';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { reloadSpace } from './content/RefreshContentSagas';
import { AddUsersToGroupAction } from '../../../userManagement/groups/services/actions/AddUsersToGroupAction';

export function addUsers(group: IGroupInfo) {
  return AddUsersToGroupAction.showAddUsersToGroupDialog(group);
}

export function* handleAddUsers(action: GroupUserAddAction): SagaIterator {
  try {
    yield call(addUsers, action.group);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function removeUsers(users: ISiteUserInfo[], spaceInfo: SpaceInfo) {
  return RemoveUsersFromGroupAction.showRemoveUsersFromGroupDialog(Set.fromIdItemArray(users), spaceInfo.item as IGroupInfo);
}

export function* handleRemoveUsers(action: GroupMenuAction): SagaIterator {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(removeUsers, action.items, spaceInfo);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function syncGroup(spaceInfo: SpaceInfo) {
  const groupInfo = spaceInfo.item as IGroupInfo;
  return ActiveDirectoryGroupAction.showSyncAdGroupDialog(groupInfo);
}

export function* handleSyncGroup(action: GroupSyncAction): SagaIterator {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(syncGroup, spaceInfo);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function syncGroups(groups: IGroupInfo[]) {
  return ActiveDirectoryGroupAction.showSyncAdGroupsDialog(groups);
}

export function* handleSyncGroups(action: GroupSyncAction): SagaIterator {
  try {
    yield call(syncGroups, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function updateMinSiteRole(groups: IGroupInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return UpdateGroupsMinimumSiteRoleAction.showUpdateGroupsMinimumSiteRoleDialog(Set.fromIdItemArray(groups), context);
}

export function* handleMinSiteRoleUpdate(action: GroupMinimumSiteRoleUpdateAction): SagaIterator {
  try {
    yield call(updateMinSiteRole, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function updateLocalGroupGrantLicenseMode(groups: IGroupInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<UpdateLocalGroupGrantLicenseModeAction>('UpdateLocalGroupGrantLicenseModeAction')
     .showUpdateGrantLicenseModeDialog(Set.fromIdItemArray(groups), context.scope);
}

export function* handleLocalGroupGrantLicenseModeUpdate(action: LocalGroupLicenseUponLoginUpdateAction): SagaIterator {
  try {
    yield call(updateLocalGroupGrantLicenseMode, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function deleteGroups(groups: IGroupInfo[]) {
  return DeleteGroupAction.showDeleteGroupDialog(Set.fromIdItemArray(groups));
}

export function* handleDeleteGroups(action: GroupsDeleteAction): SagaIterator {
  try {
    yield call(deleteGroups, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootGroupMenuActionSaga(): SagaIterator {
  yield takeEvery(GroupAddUsers, handleAddUsers);
  yield takeEvery(GroupRemoveUsers, handleRemoveUsers);
  yield takeEvery(SyncAdGroup, handleSyncGroup);
  yield takeEvery(SyncAdGroups, handleSyncGroups);
  yield takeEvery(UpdateGroupsMinimumSiteRole, handleMinSiteRoleUpdate);
  yield takeEvery(UpdateLocalGroupsLicenseUponLogin, handleLocalGroupGrantLicenseModeUpdate);
  yield takeEvery(DeleteGroups, handleDeleteGroups);
}
